import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, AlertCircle, Sparkles } from 'lucide-react';
import { useTranslation } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';

/**
 * Le formulaire de connexion de la page d'accueil.
 *
 * ========================================================================
 * POURQUOI IL EST SORTI DE LandingPage
 * ========================================================================
 *
 * La page d'accueil peut désormais venir de la base (bloc
 * « accueilConnexion »). Le texte qui entoure le formulaire devient éditable ;
 * le formulaire, lui, ne l'est pas : c'est la porte d'entrée de
 * l'application, avec sa validation et son appel à l'authentification. Il est
 * donc partagé, tel quel, entre la page d'origine et le bloc — deux copies
 * divergeraient à la première correction.
 *
 * Le JSX est celui de LandingPage, classe pour classe : la page d'origine ne
 * doit pas bouger d'un pixel.
 *
 * ========================================================================
 * L'ÉCRAN DE REDIRECTION
 * ========================================================================
 *
 * LandingPage remplace TOUTE la page par un écran de chargement après une
 * connexion réussie. Le formulaire ne peut pas le faire depuis sa place :
 *
 *   onRedirection fourni  →  le parent pose l'écran (LandingPage, le bloc)
 *   sinon                 →  le formulaire couvre l'écran lui-même
 *
 * ========================================================================
 * L'APERÇU DE L'ADMINISTRATION
 * ========================================================================
 *
 * L'admin rend les blocs de la vitrine pour son aperçu, sans AuthProvider ni
 * LanguageProvider : useAuth y lèverait et emporterait l'écran. En mode
 * `apercu`, le formulaire se montre sans toucher à aucun contexte — libellés
 * par défaut, saisie inerte.
 */

// Lu au rendu, et non figé au chargement du module : les tests peuvent ainsi
// simuler une inscription fermée sans recharger le module.
export const estInscriptionDesactivee = () => import.meta.env.VITE_DISABLE_REGISTRATION === 'true';

const validateEmail = (email) => {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
};

/**
 * L'écran plein de LandingPage pendant la redirection qui suit la connexion.
 */
export const EcranRedirection = () => {
    const { t } = useTranslation();

    return (
        <div className="min-h-screen bg-white flex items-center justify-center px-4">
            <div className="text-center">
                <div className="relative w-24 h-24 mx-auto mb-6">
                    <div className="absolute inset-0 rounded-full border-4 border-cabaza-purple/20"></div>
                    <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-cabaza-purple animate-spin"></div>
                    <div className="absolute inset-3 rounded-full border-4 border-cabaza-pink/20"></div>
                    <div className="absolute inset-3 rounded-full border-4 border-transparent border-t-cabaza-pink animate-spin" style={{ animationDirection: 'reverse', animationDuration: '0.8s' }}></div>
                    <div className="absolute inset-8 rounded-full bg-gradient-to-br from-cabaza-purple to-cabaza-pink animate-pulse"></div>
                </div>
                <p className="text-lg text-gray-600 font-medium">{t.auth?.loggingIn || 'Logging in...'}</p>
            </div>
        </div>
    );
};

/**
 * La carte elle-même, sans état ni contexte : ce qu'elle affiche lui est
 * donné. C'est ce qui permet l'aperçu hors de l'application.
 */
const CarteConnexion = ({ t, email, code, error, loading, onEmail, onCode, onSubmit, onCodeOublie }) => (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 max-w-md">
        {/* Error message */}
        {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                <p className="text-red-800 text-sm">{error}</p>
            </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
            {/* Email field */}
            <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    {t.auth?.email || 'Email address'}
                </label>
                <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                        id="email"
                        type="email"
                        value={email}
                        onChange={(e) => onEmail(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-cabaza-purple transition-colors"
                        placeholder={t.auth?.emailPlaceholder || 'example@email.com'}
                        required
                    />
                </div>
            </div>

            {/* Code field */}
            <div>
                <div className="flex justify-between items-center mb-1">
                    <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                        {t.auth?.accessCode || 'Access code'}
                    </label>
                    <button
                        type="button"
                        onClick={onCodeOublie}
                        className="text-xs text-cabaza-purple hover:text-cabaza-pink transition-colors"
                    >
                        {t.auth?.forgotCode || 'Forgot code?'}
                    </button>
                </div>
                <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                        id="code"
                        type="password"
                        value={code}
                        onChange={(e) => onCode(e.target.value)}
                        className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-cabaza-purple transition-colors"
                        placeholder="••••••"
                        required
                    />
                </div>
            </div>

            {/* Submit button */}
            <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-cabaza-purple to-cabaza-pink text-white py-3 rounded-xl font-semibold hover:shadow-lg hover:scale-[1.02] transition-all disabled:opacity-50 disabled:hover:scale-100"
            >
                {loading ? (
                    <span className="flex items-center justify-center gap-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        {t.auth?.loggingIn || 'Logging in...'}
                    </span>
                ) : (
                    t.auth?.login || 'Log in'
                )}
            </button>
        </form>

        {/* Create account link (hidden if registration is disabled) */}
        {!estInscriptionDesactivee() && (
            <div className="mt-6 pt-4 border-t border-gray-100">
                <p className="text-center text-sm text-gray-500 mb-3">
                    {t.auth?.noAccount || "Don't have an account?"}
                </p>
                <Link
                    to="/register"
                    className="flex items-center justify-center gap-2 w-full py-3 bg-gradient-to-r from-amber-400 to-orange-500 text-white rounded-xl font-semibold hover:shadow-lg hover:scale-[1.02] transition-all"
                >
                    <Sparkles className="w-5 h-5" />
                    {t.auth?.createAccountFree || 'Create a free account'}
                </Link>
            </div>
        )}
    </div>
);

/**
 * Le formulaire branché sur l'authentification : la logique de LandingPage,
 * inchangée.
 */
const FormulaireActif = ({ onRedirection }) => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { login, loading, error, setError } = useAuth();
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [isRedirecting, setIsRedirecting] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        if (!validateEmail(email)) {
            setError(t.auth?.invalidEmail || 'Invalid email address');
            return;
        }

        if (!code) {
            setError(t.auth?.enterCode || 'Please enter your code');
            return;
        }

        // Use the useAuth hook for login
        const result = await login(email, code);

        if (result.success) {
            setIsRedirecting(true);
            if (onRedirection) onRedirection();
            setTimeout(() => navigate('/'), 500);
        }
        // Error is automatically set by the hook
    };

    // Sans parent pour poser l'écran à la place de la page, il la couvre :
    // l'en-tête et le texte d'accueil disparaissent comme sur LandingPage.
    if (isRedirecting && !onRedirection) {
        return (
            <div className="fixed inset-0 z-[100]">
                <EcranRedirection />
            </div>
        );
    }

    return (
        <CarteConnexion
            t={t}
            email={email}
            code={code}
            error={error}
            loading={loading}
            onEmail={setEmail}
            onCode={setCode}
            onSubmit={handleSubmit}
            onCodeOublie={() => navigate('/login')}
        />
    );
};

const sansEffet = () => {};
const sansEnvoi = (e) => e.preventDefault();

/**
 * @param {boolean} apercu - Rendu inerte, sans contexte (aperçu de l'admin)
 * @param {Function} onRedirection - Appelé après une connexion réussie ; le
 *        parent pose alors lui-même l'écran de redirection
 */
const FormulaireConnexion = ({ apercu = false, onRedirection }) => {
    if (apercu) {
        return (
            <CarteConnexion
                t={{}}
                email=""
                code=""
                error={null}
                loading={false}
                onEmail={sansEffet}
                onCode={sansEffet}
                onSubmit={sansEnvoi}
                onCodeOublie={sansEffet}
            />
        );
    }

    return <FormulaireActif onRedirection={onRedirection} />;
};

export default FormulaireConnexion;
